import { useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { Button } from '@/components/ui/button';

import { AdjustmentNoteDialog } from './adjustment-note-dialog';
import type { AdjustmentNoteSubmitValues } from './adjustment-note-schema';
import { formatMoney } from './final-settlement-parts';
import { EditReclassifyField } from './trade-invoice-reclassify-field';
import type { FinalSettlementSubmitValues } from './final-settlement-invoice-schema';
import type { InvoiceRow } from '../types';

interface ExtrasProps {
  readonly form: UseFormReturn<FinalSettlementSubmitValues>;
  readonly existingInvoice: InvoiceRow | null;
  readonly disabled: boolean;
  readonly overpayment: boolean;
  readonly overpaidAmount: number;
  readonly side: 'buy' | 'sell';
  readonly orderId: string;
  readonly blOrderId: string;
  readonly blOrderName?: string | null;
  readonly currency: string | null;
}

// The blocks that sit between the settlement body and the dialog actions: the
// edit-only reclassify card and, when the settlement nets negative, the bridge
// to raise the overpaid amount as a Credit Note.
export function FinalSettlementDialogExtras(props: ExtrasProps) {
  return (
    <>
      <EditReclassifyField
        form={props.form}
        existingInvoice={props.existingInvoice}
        disabled={props.disabled}
        currentType="Final Settlement"
      />
      {props.overpayment && (
        <OverpaymentCreditBridge
          side={props.side}
          orderId={props.orderId}
          blOrderId={props.blOrderId}
          {...(props.blOrderName !== undefined ? { blOrderName: props.blOrderName } : {})}
          currency={props.currency}
          amount={props.overpaidAmount}
        />
      )}
    </>
  );
}

// The sell-side Credit note derives its total from categorised lines, so the
// overpaid amount is seeded as an Overpayment line; the buy-side simple layout
// takes the amount as the note total directly.
function overpaymentPrefill(
  side: 'buy' | 'sell',
  amount: number,
): Partial<AdjustmentNoteSubmitValues> {
  // Prefill copy matches Launchpad's overpayment flow verbatim.
  if (side === 'buy') {
    return { total_amount: amount, note_reason: 'Overpayment / settlement adjustment' };
  }
  return {
    note_reason: 'Overpayment / settlement adjustment',
    settlement_method: 'Offset against next invoice',
    adjustment_lines: [
      {
        type: 'Overpayment',
        description: 'Overpayment on final settlement',
        quantity: 1,
        unit: 'each',
        unit_price: amount,
        line_total: amount,
      },
    ],
  };
}

// Overpayment bridge: a fully-priced Final that nets negative still saves at 0
// due; this button opens the Credit Note flow prefilled with the overpaid
// amount so the refund is raised separately.
function OverpaymentCreditBridge(props: {
  readonly side: 'buy' | 'sell';
  readonly orderId: string;
  readonly blOrderId: string;
  readonly blOrderName?: string | null;
  readonly currency: string | null;
  readonly amount: number;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        type="button"
        variant="secondary"
        className="w-full"
        data-testid="final-settlement-create-credit-note"
        onClick={() => {
          setOpen(true);
        }}
      >
        Create Credit Note for {formatMoney(props.amount, props.currency)}
      </Button>
      <AdjustmentNoteDialog
        open={open}
        onOpenChange={setOpen}
        side={props.side}
        noteType="Credit"
        orderId={props.orderId}
        blOrderId={props.blOrderId}
        {...(props.blOrderName !== undefined ? { blOrderName: props.blOrderName } : {})}
        existingInvoice={null}
        prefillValues={overpaymentPrefill(props.side, props.amount)}
        onSaved={() => {
          setOpen(false);
        }}
      />
    </>
  );
}
